import { useRouter, usePathname, useSearchParams, useParams } from 'next/navigation'
import { useLocalStorage } from '@uidotdev/usehooks'
import { INIT_DATA_STORAGE_KEY, DEFAULT_LOCALE, TOP_CURRENCIES } from '@/app/constants'
import { UserData, Currency } from '@/app/types'
import { replaceLocaleInPathname } from '@/app/utils'
import { emitLanguageClickGAEvent, emitCurrencyClickGAEvent } from '@/app/ga-event-helpers'

export function useLanguageNavigation() {
	const router = useRouter()
	const pathname = usePathname()
	const query = useSearchParams()
	const params = useParams()

	const handleLanguageSelect = (language: string, onSuccess?: () => void) => {
		emitLanguageClickGAEvent(language)

		const currentLocale = (params.lang as string) || DEFAULT_LOCALE
		const newPathname = replaceLocaleInPathname(pathname, currentLocale, language)

		const searchParams = new URLSearchParams(query)
		const queryString = searchParams.toString()
		const url = queryString ? `${newPathname}?${queryString}` : newPathname

		router.push(url, { scroll: false })
		onSuccess?.()
	}

	return { handleLanguageSelect }
}

export function useCurrencyNavigation() {
	const router = useRouter()
	const pathname = usePathname()
	const query = useSearchParams()
	const [initData, setItem] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)

	const handleCurrencySelect = (currency: Currency, onSuccess?: () => void) => {
		emitCurrencyClickGAEvent(currency.abbreviation)

		const searchParams = new URLSearchParams(query)
		searchParams.delete('currency')
		searchParams.append('currency', currency.abbreviation)

		// Remove min/max price filters when currency changes
		if (searchParams.has('mn')) searchParams.delete('mn')
		if (searchParams.has('mx')) searchParams.delete('mx')

		setItem({
			...initData,
			selectedCurrency: currency,
		})

		router.push(`${pathname}?${searchParams.toString()}`)
		onSuccess?.()
	}

	return { handleCurrencySelect }
}

export function useCurrentCurrency() {
	const query = useSearchParams()
	const [initData] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)

	const searchParams = new URLSearchParams(query)
	const currencyFromUrl = searchParams.get('currency')
	const currencyFromStorage = initData?.selectedCurrency?.abbreviation
	const localCurrency = initData?.defaultCurrency?.abbreviation
	const fallbackCurrency = TOP_CURRENCIES[0].abbreviation

	return currencyFromUrl || currencyFromStorage || localCurrency || fallbackCurrency
}

export function useCurrentLanguage() {
	const params = useParams()
	return (params.lang as string) || DEFAULT_LOCALE
}

export function useCombinedLanguageAndCurrencyNavigation() {
	const router = useRouter()
	const pathname = usePathname()
	const query = useSearchParams()
	const params = useParams()
	const [initData, setItem] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)

	const handleBothSelect = (language: string, currency: Currency, onSuccess?: () => void) => {
		emitLanguageClickGAEvent(language)
		emitCurrencyClickGAEvent(currency.abbreviation)

		setItem({
			...initData,
			selectedCurrency: currency,
		})

		const currentLocale = (params.lang as string) || DEFAULT_LOCALE
		const newPathname = replaceLocaleInPathname(pathname, currentLocale, language)

		const searchParams = new URLSearchParams(query)
		searchParams.delete('currency')
		searchParams.append('currency', currency.abbreviation)

		if (searchParams.has('mn')) searchParams.delete('mn')
		if (searchParams.has('mx')) searchParams.delete('mx')

		const queryString = searchParams.toString()
		const url = queryString ? `${newPathname}?${queryString}` : newPathname

		router.push(url, { scroll: false })
		onSuccess?.()
	}

	return { handleBothSelect }
}

